import * as React from 'react';
import { isDefined, noop } from 'react-financial-charts';
import { getXValue } from '@react-financial-charts/core/lib/utils/ChartDataUtil';
import { saveNodeType } from 'react-financial-charts';
import { HoverTextNearMouse } from '@react-financial-charts/interactive/lib/components/index';
import { InteractiveStraightLine, ClickableCircle } from '.';
import { FilledArea } from './FilledArea';
import type { EachFibChannelProps } from './EachFibChannel';
import type { MoreProps } from '@/types/commontypes';

interface TriangleDragStart {
  chartId: unknown;
  startXY: number[] | undefined;
  endXY: number[] | undefined;
  dy: number | undefined;
  endXY2: number[] | undefined;
}

interface EachTriangleState {
  hover: boolean;
  dragging: boolean;
}

export class EachTriangle extends React.Component<EachFibChannelProps, EachTriangleState> {
  public static defaultProps = {
    yDisplayFormat: (d: number) => d.toFixed(2),
    interactive: true,
    selected: false,
    onDrag: noop,
    hoverText: {
      enable: false,
    },
  };

  private dragStart: TriangleDragStart | undefined;
  // @ts-expect-error
  private lineInteractive: string;
  private saveNodeType: <T>(type: string) => React.Ref<T>;

  public constructor(props: EachFibChannelProps) {
    super(props);

    this.saveNodeType = saveNodeType.bind(this);

    this.state = {
      hover: false,
      dragging: false,
    };
  }

  public render() {
    const { startXY, endXY, dy, fill, stroke, onDoubleClick } = this.props;
    const { hoverText, appearance, endXY2 } = this.props;
    const { edgeFill, edgeFill2: _edgeFill2 } = appearance;
    const { selected } = this.props;
    const { hover } = this.state;
    const { enable: hoverTextEnabled, ...restHoverTextProps } = hoverText;
    const line1Edge =
      isDefined(startXY) && isDefined(endXY) ? (
        <g>
          {this.getEdgeCircle({
            xy: startXY,
            dragHandler: this.handleLine1Edge1Drag,
            cursor: 'react-financial-charts-ns-resize-cursor',
            fill: edgeFill,
            edge: 'line1edge1',
          })}
          {this.getEdgeCircle({
            xy: endXY,
            dragHandler: this.handleLine1Edge2Drag,
            cursor: 'react-financial-charts-ns-resize-cursor',
            fill: edgeFill,
            edge: 'line1edge2',
          })}
        </g>
      ) : null;
    const line2Edge =
      endXY2 !== undefined && isDefined(dy) ? (
        <g>
          {this.getEdgeCircle({
            xy: endXY2,
            dragHandler: this.handleChannelHeightChange,
            cursor: 'react-financial-charts-ns-resize-cursor',
            fill: edgeFill,
            edge: 'line2edge1',
          })}
          {/* {this.getEdgeCircle({
                        xy: endXY2 ?? [endXY[0], endXY[1] + dy]                        ,
                        dragHandler: this.handleChannelHeightChange,
                        cursor: "react-financial-charts-ns-resize-cursor",
                        fill: edgeFill2,
                        edge: "line2edge2",
                    })} */}
        </g>
      ) : null;
    return (
      <g>
        {/* Lines */}
        {endXY && this.renderLine(startXY[0], startXY[1], endXY[0], endXY[1])}
        {endXY && this.renderLine(endXY[0], endXY[1], endXY2?.[0], endXY2?.[1])}
        {endXY2 && this.renderLine(startXY[0], startXY[1], endXY2?.[0], endXY2?.[1])}

        {/* Filled Area */}
        {endXY2 && isDefined(dy) && (
          <FilledArea
            x1Value={startXY[0]}
            y1Value={startXY[1]}
            x2Value={endXY[0]}
            y2Value={endXY[1]}
            x3Value={endXY2[0]}
            y3Value={endXY2[1]}
            selected={selected || hover}
            onDragStart={this.handleDragStart}
            onDrag={this.handleChannelDrag}
            onDragComplete={this.handleDragComplete}
            onMouseDown={this.handleShapeMouseDown}
            strokeStyle={stroke!}
            strokeWidth={appearance.strokeWidth}
            strokeDasharray={appearance.strokeDasharray}
            extendLeft={appearance.extendLeft}
            extendRight={appearance.extendRight}
            fillStyle={fill || 'rgba(156, 39, 176, 0.2)'}
            type="LINE"
            onDoubleClick={onDoubleClick}
          />
        )}
        {line1Edge}
        {line2Edge}
        <HoverTextNearMouse show={hoverTextEnabled && hover && !selected} {...restHoverTextProps} />
      </g>
    );
  }

  private renderLine = (x1: number, y1: number, x2: number | undefined, y2: number | undefined) => {
    const { stroke, selected, appearance, onDoubleClick } = this.props;

    const { strokeWidth, strokeDasharray } = appearance;
    const { hover, dragging } = this.state;

    if (dragging) {
      this.lineInteractive = 'react-financial-charts-grabbing-cursor';
    } else if (!dragging) {
      this.lineInteractive = 'react-financial-charts-pointer-cursor';
    } else {
      this.lineInteractive = 'react-financial-charts-pointer-cursor';
    }

    return (
      <InteractiveStraightLine
        ref={this.saveNodeType('triangle-line')}
        selected={selected || hover}
        onHover={this.handleHover}
        onUnHover={this.handleHover}
        x1Value={x1}
        y1Value={y1}
        x2Value={x2}
        y2Value={y2}
        type="LINE"
        strokeStyle={stroke!}
        strokeWidth={strokeWidth}
        strokeDasharray={strokeDasharray}
        interactiveCursorClass={this.lineInteractive}
        onDragStart={this.handleDragStart}
        onDrag={this.handleChannelDrag}
        onDragComplete={this.handleDragComplete}
        onMouseDown={this.handleShapeMouseDown}
        extendLeft={false}
        extendRight={false}
        onDoubleClick={onDoubleClick}
      />
    );
  };

  private readonly handleShapeMouseDown = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { onSelect, uuid, sharedState } = this.props;
    if (sharedState?.status === 'started') return;
    if (onSelect && isDefined(uuid)) {
      onSelect(e, uuid, moreProps);
    }
  };

  private readonly getEdgeCircle = ({
    xy,
    dragHandler,
    fill,
    edge,
  }: {
    xy: number[];
    dragHandler: (e: React.MouseEvent, moreProps: MoreProps) => void;
    fill: string;
    edge: string;
    cursor?: string;
  }) => {
    const { hover } = this.state;
    const { appearance, onDoubleClick } = this.props;
    const { edgeStroke, edgeStrokeWidth, r } = appearance;
    const { selected } = this.props;
    const { onDragComplete, locked } = this.props;
    const lockedR = locked ? r - 2 : r;
    const lockedEdgeStorke = locked ? '#436fe9' : edgeStroke;
    const lockedStrokeWidth = locked ? 1 : edgeStrokeWidth;
    return (
      <ClickableCircle
        ref={this.saveNodeType(edge)}
        show={selected || hover || locked}
        cx={xy[0]}
        cy={xy[1]}
        r={lockedR}
        fillStyle={fill}
        strokeStyle={lockedEdgeStorke}
        strokeWidth={lockedStrokeWidth}
        interactiveCursorClass="big-declined-arrow-cursor"
        onDragStart={this.handleDragStart}
        onDrag={dragHandler}
        onDragComplete={onDragComplete}
        onDoubleClick={onDoubleClick}
      />
    );
  };

  private readonly handleDragComplete = (e: React.MouseEvent, moreProps: MoreProps) => {
    this.setState({
      dragging: false,
    });
    const { onDragComplete, locked } = this.props;
    if (onDragComplete === undefined || locked) {
      return;
    }
    onDragComplete(e, moreProps);
  };

  private readonly handleChannelHeightChange = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, onDragStart, locked, chartId: _chartId } = this.props;
    if (locked || !this.dragStart) return;
    if (onDragStart && uuid !== undefined) {
      onDragStart(uuid);
    }
    const { startXY, endXY, dy } = this.dragStart;

    const {
      chartConfig: { yScale },
      mouseXY,
      xAccessor,
      xScale,
      fullData,
    } = moreProps;
    const hovering = moreProps.hovering || false;
    const startPos = moreProps.startPos!;

    if (!hovering) return;

    const dx = startPos[0] - mouseXY[0];
    const dy2 = startPos[1] - mouseXY[1];

    const origEndXY2 = this.dragStart.endXY2;
    const x3 = xScale(origEndXY2![0]);
    const y3 = yScale(origEndXY2![1]);

    const newX3Value = getXValue(xScale, xAccessor, [x3 - dx, y3 - dy2], fullData);
    const newY3Value = yScale.invert(y3 - dy2);

    onDrag(e, uuid, {
      startXY,
      endXY,
      dy,
      endXY2: [newX3Value, newY3Value],
    });
  };

  private readonly handleLine1Edge2Drag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, onDragStart, locked, chartId: _chartId } = this.props;

    if (locked || !this.dragStart) return;

    const { endXY, endXY2 } = this.dragStart;
    if (onDragStart && uuid !== undefined) {
      onDragStart(uuid);
    }
    const {
      mouseXY,
      xAccessor,
      xScale,
      fullData,
      chartConfig: { yScale },
    } = moreProps;
    const startPos = moreProps.startPos!;
    const hovering = moreProps.hovering || false;

    if (!hovering) return;
    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const x1 = xScale(endXY![0]);
    const y1 = yScale(endXY![1]);

    const newX1Value = getXValue(xScale, xAccessor, [x1 - dx, y1 - dy], fullData);
    const newY1Value = yScale.invert(y1 - dy);

    onDrag(e, uuid, {
      startXY: this.dragStart.startXY,
      endXY: [newX1Value, newY1Value],
      dy: this.dragStart.dy,
      endXY2,
    });
  };

  private readonly handleLine1Edge1Drag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, onDragStart, locked } = this.props;

    if (
      locked ||
      !this.dragStart
      // this.dragStart.chartId !== moreProps.chartId
    )
      return;

    if (onDragStart && uuid !== undefined) {
      onDragStart(uuid);
    }

    const { startXY, endXY2 } = this.dragStart;

    const {
      mouseXY,
      xAccessor,
      xScale,
      fullData,
      chartConfig: { yScale },
    } = moreProps;
    const startPos = moreProps.startPos!;
    const hovering = moreProps.hovering || false;

    if (!hovering) return;

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const x1 = xScale(startXY![0]);
    const y1 = yScale(startXY![1]);

    const newX1Value = getXValue(xScale, xAccessor, [x1 - dx, y1 - dy], fullData);
    const newY1Value = yScale.invert(y1 - dy);

    onDrag(e, uuid, {
      startXY: [newX1Value, newY1Value],
      endXY: this.dragStart.endXY,
      dy: this.dragStart.dy,
      endXY2,
    });
  };

  private readonly handleChannelDrag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, locked } = this.props;
    if (
      !this.dragStart ||
      // this.dragStart.chartId !== moreProps.chartId ||
      locked
    )
      return;
    const { startXY, endXY, endXY2 } = this.dragStart;

    const {
      xScale,
      chartConfig: { yScale },
      xAccessor,
      fullData,
      mouseXY,
    } = moreProps;
    const hovering = moreProps.hovering || false;
    const startPos = moreProps.startPos!;
    const _yDomain = yScale.domain();

    if (!hovering) return;
    const x1 = xScale(startXY![0]);
    const y1 = yScale(startXY![1]);
    const x2 = xScale(endXY![0]);
    const y2 = yScale(endXY![1]);

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const newX1Value = getXValue(xScale, xAccessor, [x1 - dx, y1 - dy], fullData);
    const newX2Value = getXValue(xScale, xAccessor, [x2 - dx, y2 - dy], fullData);
    const newY1Value = yScale.invert(y1 - dy);
    const newY2Value = yScale.invert(y2 - dy);

    const deltaX = newX2Value - endXY![0];
    const deltaY = newY2Value - endXY![1];

    const newEndXY2 = endXY2 ? [endXY2[0] + deltaX, endXY2[1] + deltaY] : undefined;

    this.setState({ dragging: true });
    onDrag(e, uuid, {
      startXY: [newX1Value, newY1Value],
      endXY: [newX2Value, newY2Value],
      dy: this.dragStart.dy,
      endXY2: newEndXY2,
    });
  };

  private readonly handleDragStart = (_: React.MouseEvent, moreProps: MoreProps) => {
    const { startXY, endXY, dy, endXY2, onDragStart, uuid, locked } = this.props;
    if (locked) return;
    if (onDragStart && uuid !== undefined) {
      onDragStart(uuid);
    }
    this.setState({ dragging: true });
    this.dragStart = {
      chartId: moreProps.chartId,
      startXY,
      endXY,
      dy,
      endXY2,
    };
  };

  private readonly handleHover = (_: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onHover, sharedState, isAnyShapeEnabled } = this.props;
    if (sharedState?.status === 'started' || isAnyShapeEnabled) return;
    const hovering = moreProps.hovering || false;
    if (this.state.hover !== hovering) {
      this.setState({
        hover: hovering,
      });
      if (onHover) {
        onHover(hovering ? (uuid ?? null) : null, moreProps);
      }
    }
  };
}
